import { useTheme } from '@/context/ThemeContext';

const problems = [
  { icon: 'ri-phone-line', text: 'Missed inbound calls = lost jobs', sub: 'Up to 62% of calls go unanswered during peak hours' },
  { icon: 'ri-time-line', text: 'Slow follow-ups reduce conversions', sub: 'Response time over 5 minutes drops conversion by 80%' },
  { icon: 'ri-flow-chart', text: 'Disorganized pipelines lead to leakage', sub: 'Leads fall through without structured tracking' },
  { icon: 'ri-team-line', text: 'Staff overload limits growth', sub: 'Your team can\'t scale manually without adding headcount' },
];

export default function ProblemSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section
      id="problem"
      className={`relative py-28 md:py-36 overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#050505]' : 'bg-white'}`}
    >
      <div className={`absolute top-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-white/8 to-transparent' : 'bg-gradient-to-r from-transparent via-black/8 to-transparent'}`} />

      {/* Background image */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src="https://public.readdy.ai/ai/img_res/f93c057e765454a26d5bd3dbbba9467a.jpg"
          alt=""
          className={`w-full h-full object-cover object-center transition-opacity duration-500 ${isDark ? 'opacity-[0.06]' : 'opacity-[0.03]'}`}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-px bg-red-400/60"></div>
          <span className="text-xs font-grotesk text-red-400 tracking-widest uppercase">The Problem</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <div>
            <h2 className={`font-sora font-800 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>
              Missed Opportunities<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">
                Are Costing You
              </span>
            </h2>
            <p className={`font-grotesk text-lg leading-relaxed mb-5 ${isDark ? 'text-white/55' : 'text-[#1D1D1F]/55'}`}>
              In service businesses, every missed call, delayed response, or untracked inquiry results in lost revenue. Your team is busy in the field, in meetings, or serving clients—while opportunities slip through the cracks.
            </p>
            <p className={`font-grotesk text-lg leading-relaxed ${isDark ? 'text-white/55' : 'text-[#1D1D1F]/55'}`}>
              The problem is not demand. The problem is{' '}
              <strong className={isDark ? 'text-white font-600' : 'text-[#1D1D1F] font-600'}>capture and response speed.</strong>
            </p>

            {/* Visual stat */}
            <div className={`mt-10 p-6 rounded-2xl border ${isDark ? 'border-red-500/15 bg-red-500/[0.04]' : 'border-red-500/15 bg-red-500/[0.03]'}`}>
              <div className="font-sora font-800 text-6xl text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400 mb-2">62%</div>
              <p className={`font-grotesk text-sm ${isDark ? 'text-white/50' : 'text-black/50'}`}>
                of inbound calls go unanswered during peak business hours — each one a potential job lost to a competitor who picked up.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {problems.map((item, i) => (
              <div
                key={i}
                className={`group flex items-start gap-5 p-6 rounded-2xl border transition-all duration-300 cursor-default ${
                  isDark
                    ? 'bg-white/[0.02] border-white/5 hover:border-red-500/20 hover:bg-red-500/[0.03]'
                    : 'bg-[#F5F5F7] border-black/5 hover:border-red-500/20 hover:bg-red-500/[0.03]'
                }`}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/20 flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-red-400 text-lg`}></i>
                </div>
                <div>
                  <p className={`font-grotesk font-600 text-base mb-1 ${isDark ? 'text-white/85' : 'text-[#1D1D1F]/85'}`}>{item.text}</p>
                  <p className={`font-grotesk text-sm ${isDark ? 'text-white/40' : 'text-black/40'}`}>{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`absolute bottom-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-white/5 to-transparent' : 'bg-gradient-to-r from-transparent via-black/5 to-transparent'}`} />
    </section>
  );
}
